import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Award, 
  Target, 
  Heart, 
  MapPin, 
  Phone, 
  Mail, 
  Calendar,
  TrendingUp,
  Building2,
  Globe,
  Shield,
  Zap,
  ArrowRight,
  CheckCircle,
  Star,
  Linkedin,
  Github,
  Twitter,
  Send,
  User,
  Building,
  MessageSquare,
  BarChart3
} from 'lucide-react';

import { Container, Button, Card, SectionTitle } from '../components';
import { useInView, useCountUp } from '../hooks';

/**
 * 关于我们页面
 * 按照需求文档展示公司介绍、团队展示、发展历程、企业文化、联系方式
 */
const AboutPage: React.FC = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [statsRef, statsInView] = useInView({ threshold: 0.3 });
  const [aggregationRef, aggregationInView] = useInView({ threshold: 0.2 });
  const [casesRef, casesInView] = useInView({ threshold: 0.2 });
  const [timelineRef, timelineInView] = useInView({ threshold: 0.2 });
  const [contactRef, contactInView] = useInView({ threshold: 0.2 });
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    contactType: 'phone' as 'phone' | 'email',
    contact: '',
    solution: '',
    description: ''
  });



  // 平台实力数据统计
  const stats = [
    {
      icon: <MapPin className="w-8 h-8" />,
      number: useCountUp(statsInView, 350),
      label: '覆盖城市',
      suffix: '+'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      number: useCountUp(statsInView, 50),
      label: '合作服务商',
      suffix: '+'
    },
    {
      icon: <Users className="w-8 h-8" />,
      number: useCountUp(statsInView, 100),
      label: '合作伙伴',
      suffix: '+'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      number: useCountUp(statsInView, 30),
      label: '平均流量增长',
      suffix: '%'
    }
  ];

  // 四大核心能力
  const coreCapabilities = [
    {
      icon: <Target className="w-12 h-12" />,
      title: '流量聚合',
      description: '整合多渠道流量资源，为充电/加油服务商提供精准用户匹配，实现流量价值最大化'
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: '营销赋能',
      description: '提供能量币、优惠券、营销玩法等多元化营销工具，助力合作伙伴提升用户粘性和转化率'
    },
    {
      icon: <Globe className="w-12 h-12" />,
      title: '供给保障',
      description: '构建完善的车后服务供应链体系，确保充电、加油、维修保养等服务的稳定供给'
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: '生态变现',
      description: '通过数据分析和智能调度，优化资源配置，为合作伙伴创造更多商业价值和收益机会'
    }
  ];

  // 合作伙伴案例
  const partnerCases = [
    {
      logo: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=charging%20station%20company%20logo%20blue%20electric&image_size=square',
      name: '星充科技',
      industry: '充电服务商',
      result: '流量增长40%',
      description: '通过车为家流量聚合平台，实现精准用户匹配，月活用户增长40%，充电订单量提升35%'
    },
    {
      logo: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=gas%20station%20company%20logo%20green%20fuel&image_size=square',
      name: '绿能加油',
      industry: '加油服务商',
      result: '营收增长35%',
      description: '借助营销赋能工具和流量聚合服务，新客户转化率提升35%，月营收增长显著'
    },
    {
      logo: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=taxi%20company%20logo%20orange%20transport&image_size=square',
      name: '城市出行',
      industry: '出租车公司',
      result: '成本降低30%',
      description: '通过车为家一站式车后生态服务，运营成本降低30%，司机满意度和车队效率大幅提升'
    },
    {
      logo: 'https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=fleet%20management%20logo%20purple%20modern&image_size=square',
      name: '智行车队',
      industry: '车队管理',
      result: '效率提升50%',
      description: '采用车为家供给保障和生态变现方案，车队运营效率提升50%，维护成本显著降低'
    }
  ];

  // 核心价值观
  const values = [
    {
      icon: Shield,
      title: '诚信透明',
      description: '建立透明可信的平台机制，保障所有合作伙伴的权益'
    },
    {
      icon: Zap,
      title: '高效聚合',
      description: '通过智能算法实现精准匹配，提升流量转化效率'
    },
    {
      icon: Heart,
      title: '共赢合作',
      description: '致力于为服务商和车队创造双赢的商业价值'
    },
    {
      icon: TrendingUp,
      title: '数据驱动',
       description: '基于大数据分析优化匹配策略，持续提升平台价值'
     }
   ];



  // 发展历程
  const timeline = [
    {
      year: '2022',
      title: '公司成立',
      description: '车为家正式成立，专注汽车后市场数字化服务。平台服务覆盖全国主要城市，建立完整的流量聚合生态体系。'
    },
    {
      year: '2023',
      title: '行业领先',
      description: '成为汽车后市场流量聚合领域的领军企业，平台价值持续提升。合作服务商加网约车平台超过100家，能量币营销补贴超过100万元。'
    },
    {
      year: '2024',
      title: '智能升级',
      description: '深度应用AI技术优化匹配算法，打造更智能的流量聚合平台，AI算法全面升级，匹配效率提升50%，生态价值持续放大。'
    },
    {
      year: '2025',
      title: '生态完善',
      description: '持续创新技术和服务模式，引领汽车后市场数字化转型。构建完整的汽车后市场服务生态，成为行业领导者。全面拥抱数字化转型，生态体系进一步完善。'
    }
  ];

  // 荣誉资质
  const honors = [
    {
      title: '国家高新技术企业',
      year: '2023',
      icon: Award
    },
    {
      title: '最佳汽车科技创新奖',
      year: '2023',
      icon: Star
    },
    {
      title: 'ISO 27001信息安全认证',
      year: '2022',
      icon: Shield
    },
    {
      title: '优秀软件产品奖',
      year: '2022',
      icon: Award
    }
  ];

  // 表单提交处理
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // 表单验证
    if (!formData.name.trim()) {
      alert('请输入联系人姓名');
      return;
    }
    if (!formData.contact.trim()) {
      alert('请输入联系方式');
      return;
    }
    if (!formData.company.trim()) {
      alert('请输入公司名称');
      return;
    }
    if (!formData.solution) {
      alert('请选择解决方案');
      return;
    }
    
    // 联系方式格式验证
    if (formData.contactType === 'phone') {
      const phoneRegex = /^1[3-9]\d{9}$/;
      if (!phoneRegex.test(formData.contact)) {
        alert('请输入正确的手机号码格式');
        return;
      }
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact)) {
        alert('请输入正确的邮箱格式');
        return;
      }
    }
    
    // 这里可以添加表单提交逻辑
    console.log('表单数据:', formData);
    alert('感谢您的咨询，我们会尽快与您联系！');
    
    // 重置表单
    setFormData({
      name: '',
      company: '',
      contactType: 'phone' as 'phone' | 'email',
      contact: '',
      solution: '',
      description: ''
    });
  };

  return (
    <div className="overflow-hidden">
      {/* 英雄区 */}
      <section ref={heroRef} className="relative pt-20 pb-16 bg-gradient-to-br from-[#F0F7FF] to-[#E6F0FF] overflow-hidden">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="text-left"
            >
              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                车为家—乾三科技
                <span className="text-[#1677FF] block mt-2">智能服务平台</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                我们是专业的汽车后市场智能服务平台，连接供需双端，
                通过智能匹配和数据驱动，为双方创造更大商业价值
              </p>
              {/* 三个按钮组 - 从首页移动过来 */}
              <div className="pt-4">
                <div className="flex flex-col sm:flex-row gap-4 max-w-3xl">
                  {/* 主要行动按钮 */}
                  <div className="flex flex-col sm:flex-row gap-3 flex-1">
                    <button 
                      className="group bg-gradient-to-r from-[#1677FF] to-[#0958d9] hover:from-[#0958d9] hover:to-[#1677FF] text-white px-8 py-5 text-base font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex-1 min-h-[70px] flex items-center justify-center relative overflow-hidden"
                      onClick={() => window.location.href = '/car-service'}
                    >
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                      <div className="flex items-center justify-center space-x-3 relative z-10">
                        <div className="text-center">
                          <div className="text-lg font-bold">车后服务</div>
                          <div className="text-sm opacity-90">解决方案</div>
                        </div>
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                    
                    <button 
                      className="group bg-gradient-to-r from-[#722ED1] to-[#531DAB] hover:from-[#531DAB] hover:to-[#722ED1] text-white px-8 py-5 text-base font-semibold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 flex-1 min-h-[70px] flex items-center justify-center relative overflow-hidden"
                      onClick={() => window.location.href = '/rental-service'}
                    >
                      <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                      <div className="flex items-center justify-center space-x-3 relative z-10">
                        <div className="text-center">
                          <div className="text-lg font-bold">租车服务</div>
                          <div className="text-sm opacity-90">解决方案</div>
                        </div>
                        <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>
                  
                  {/* 次要行动按钮 */}
                  <button 
                    className="group border-2 border-[#1677FF] text-[#1677FF] hover:bg-[#1677FF] hover:text-white px-8 py-5 text-base font-semibold rounded-xl transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1 min-h-[70px] flex items-center justify-center sm:w-48"
                    onClick={() => {
                      // 这里可以添加咨询弹窗逻辑
                      const contactSection = document.getElementById('contact');
                      contactSection?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    <svg className="mr-3 w-5 h-5 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="font-bold">免费咨询</span>
                  </button>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={heroInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=modern%20office%20building%20with%20glass%20facade%2C%20professional%20corporate%20headquarters%2C%20blue%20sky%2C%20clean%20architecture%2C%20business%20district&image_size=landscape_4_3" 
                  alt="车为家公司总部" 
                  className="w-full h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
              {/* 装饰元素 */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-[#1677FF]/10 rounded-full blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-blue-200/30 rounded-full blur-xl"></div>
            </motion.div>
          </div>
        </Container>
      </section>



      {/* 平台实力数据统计 */}
      <section ref={statsRef} className="py-12 bg-gradient-to-br from-blue-50 to-indigo-50">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <Card className="p-6 hover:shadow-lg transition-all duration-300 group">
                  <div className="text-blue-600 mb-3 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {stat.icon}
                  </div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">
                    {stat.number}{stat.suffix}
                  </div>
                  <div className="text-gray-600 font-medium text-sm">
                    {stat.label}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* 四大核心能力 */}
      <section ref={aggregationRef} className="py-20">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={aggregationInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <SectionTitle 
              title="四大核心能力" 
              subtitle="专业能力驱动行业发展" 
            />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreCapabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={aggregationInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full hover:shadow-xl transition-all duration-300 group text-center">
                  <div className="text-blue-600 mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {capability.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {capability.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* 合作伙伴案例 */}
      <section ref={casesRef} className="py-20 bg-gray-50">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={casesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <SectionTitle 
              title="合作伙伴案例" 
              subtitle="携手共创，成就彼此" 
            />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerCases.map((partner, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={casesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="p-6 h-full hover:shadow-xl transition-all duration-300 group text-center">
                  <div className="flex items-center justify-center mb-4">
                    <img 
                      src={partner.logo} 
                      alt={partner.name}
                      className="w-12 h-12 rounded-lg object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {partner.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3">
                      {partner.industry}
                    </p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 mb-3">
                    <div className="text-lg font-bold text-blue-600">
                      {partner.result}
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-xs">
                    {partner.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

        {/* 合作伙伴Logo墙 */}
        <section className="py-20 bg-gray-50">
          <Container>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                我们的合作伙伴
              </h2>
              <p className="text-xl text-gray-600">
                与行业领先企业携手共进
              </p>
            </motion.div>

            <div className="space-y-8">
              {/* 第一行 */}
              <div className="overflow-hidden">
                <motion.div
                  className="flex space-x-8"
                  animate={{ x: ["-100%", "0%"] }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  {[...Array(2)].map((_, setIndex) => (
                    <div key={setIndex} className="flex space-x-8 min-w-full">
                      {[
                        { name: "华为", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Huawei%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "腾讯", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Tencent%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "阿里巴巴", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Alibaba%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "百度", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Baidu%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "京东", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=JD%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "美团", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Meituan%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "苹果", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Apple%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "微软", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Microsoft%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "谷歌", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Google%20company%20logo%20clean%20modern%20design&image_size=square" }
                      ].map((partner, index) => (
                        <div key={index} className="flex-shrink-0 w-32 h-20 bg-white rounded-lg shadow-sm flex items-center justify-center hover:shadow-md transition-shadow duration-300">
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="max-w-24 max-h-12 object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* 第二行 */}
              <div className="overflow-hidden">
                <motion.div
                  className="flex space-x-8"
                  animate={{ x: ["0%", "-100%"] }}
                  transition={{
                    duration: 25,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  {[...Array(2)].map((_, setIndex) => (
                    <div key={setIndex} className="flex space-x-8 min-w-full">
                      {[
                        { name: "滴滴", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Didi%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "小米", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Xiaomi%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "字节跳动", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=ByteDance%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "网易", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=NetEase%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "新浪", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Sina%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "搜狐", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Sohu%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "亚马逊", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Amazon%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "特斯拉", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Tesla%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "比亚迪", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=BYD%20company%20logo%20clean%20modern%20design&image_size=square" }
                      ].map((partner, index) => (
                        <div key={index} className="flex-shrink-0 w-32 h-20 bg-white rounded-lg shadow-sm flex items-center justify-center hover:shadow-md transition-shadow duration-300">
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="max-w-24 max-h-12 object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </motion.div>
              </div>

              {/* 第三行 */}
              <div className="overflow-hidden">
                <motion.div
                  className="flex space-x-8"
                  animate={{ x: ["-100%", "0%"] }}
                  transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                >
                  {[...Array(2)].map((_, setIndex) => (
                    <div key={setIndex} className="flex space-x-8 min-w-full">
                      {[
                        { name: "携程", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Ctrip%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "去哪儿", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Qunar%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "途牛", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Tuniu%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "同程", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Tongcheng%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "飞猪", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Fliggy%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "马蜂窝", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Mafengwo%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "蔚来", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=NIO%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "理想", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Li%20Auto%20company%20logo%20clean%20modern%20design&image_size=square" },
                        { name: "小鹏", logo: "https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=XPeng%20company%20logo%20clean%20modern%20design&image_size=square" }
                      ].map((partner, index) => (
                        <div key={index} className="flex-shrink-0 w-32 h-20 bg-white rounded-lg shadow-sm flex items-center justify-center hover:shadow-md transition-shadow duration-300">
                          <img
                            src={partner.logo}
                            alt={partner.name}
                            className="max-w-24 max-h-12 object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                          />
                        </div>
                      ))}
                    </div>
                  ))}
                </motion.div>
              </div>
            </div>
          </Container>
        </section>

        {/* 企业文化 */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-transparent"></div>
        <div className="absolute top-20 right-10 w-64 h-64 bg-blue-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-48 h-48 bg-[#1677FF]/10 rounded-full blur-2xl"></div>
        
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <SectionTitle
                title="企业文化"
                subtitle="我们的核心价值观指引着每一个决策和行动"
                centered={false}
              />
              <p className="text-gray-600 mt-6 leading-relaxed">
                车为家始终坚持以客户为中心，以技术创新为驱动力，致力于为汽车后市场提供最优质的数字化服务解决方案。
              </p>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=Chinese%20business%20professionals%20meeting%20in%20conference%20room%2C%20Asian%20team%20collaboration%2C%20modern%20office%20boardroom%2C%20corporate%20discussion%2C%20professional%20atmosphere&image_size=landscape_4_3" 
                  alt="团队协作" 
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={heroInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                >
                  <Card className="p-6 text-center h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white/80 backdrop-blur-sm">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 bg-gradient-to-br from-[#1677FF] to-blue-600 text-white shadow-lg">
                      <IconComponent className="w-7 h-7" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>



      {/* 发展历程 */}
      <section ref={timelineRef} className="py-12 bg-gradient-to-br from-gray-50 to-blue-50/30 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-100/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#1677FF]/10 rounded-full blur-2xl"></div>
        
        <Container className="relative z-10">
          <div className="text-center mb-16">
            <SectionTitle
              title="发展历程"
              subtitle="从初创到行业领先，见证我们的成长足迹"
              centered
            />

          </div>
          
          <div className="mt-16">
            <div className="relative">
              {/* 时间线 */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-[#1677FF] to-blue-300"></div>
              
              <div className="space-y-8">
                {timeline.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                    animate={timelineInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                    className={`relative flex items-center ${
                      index % 2 === 0 ? 'justify-start' : 'justify-end'
                    }`}
                  >
                    {/* 时间节点 */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-6 h-6 rounded-full border-4 border-white shadow-xl z-10 bg-gradient-to-br from-[#1677FF] to-blue-600"></div>
                    
                    {/* 内容卡片 */}
                    <div className={`w-5/12 ${
                      index % 2 === 0 ? 'pr-8' : 'pl-8'
                    }`}>
                      <Card className="p-6 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white/90 backdrop-blur-sm border border-blue-100">
                        <div className="flex items-center gap-4 mb-4">
                          <div className="bg-gradient-to-br from-[#1677FF] to-blue-600 text-white px-4 py-2 rounded-full">
                            <span className="text-xl font-bold">{item.year}</span>
                          </div>
                          <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                        </div>
                        <p className="text-gray-600 leading-relaxed">{item.description}</p>
                        {/* 添加小图标装饰 */}
                        <div className="mt-4 flex gap-2">
                          <div className="w-2 h-2 rounded-full bg-[#1677FF]"></div>
                          <div className="w-2 h-2 rounded-full bg-blue-300"></div>
                          <div className="w-2 h-2 rounded-full bg-blue-200"></div>
                        </div>
                      </Card>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* 荣誉资质 */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute top-10 right-0 w-72 h-72 bg-gradient-to-l from-blue-50 to-transparent rounded-full"></div>
        <div className="absolute bottom-10 left-0 w-64 h-64 bg-gradient-to-r from-[#1677FF]/5 to-transparent rounded-full"></div>
        
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <div>
              <SectionTitle
                title="荣誉资质"
                subtitle="获得行业权威认可，彰显专业实力"
                centered={false}
              />
              <p className="text-gray-600 mt-6 leading-relaxed">
                凭借卓越的技术实力和优质的服务品质，车为家获得了多项行业权威认证和荣誉奖项，这些认可见证了我们的专业能力和市场地位。
              </p>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=professional%20award%20ceremony%2C%20business%20achievement%20trophy%2C%20corporate%20recognition%2C%20golden%20award%2C%20success%20celebration&image_size=square" 
                    alt="荣誉奖项" 
                    className="w-full h-32 object-cover"
                  />
                </div>
                <div className="rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src="https://trae-api-sg.mchost.guru/api/ide/v1/text_to_image?prompt=ISO%20certification%20document%2C%20quality%20management%20certificate%2C%20professional%20accreditation%2C%20business%20standards&image_size=square" 
                    alt="资质认证" 
                    className="w-full h-32 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {honors.map((honor, index) => {
              const IconComponent = honor.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={timelineInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="p-6 text-center h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-gradient-to-br from-white to-blue-50/30 border border-blue-100">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg">
                      <IconComponent className="w-8 h-8" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">{honor.title}</h3>
                    <div className="inline-block px-3 py-1 bg-[#1677FF] text-white text-sm rounded-full">
                      {honor.year}
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* 联系我们 */}
      <section ref={contactRef} className="py-20 bg-gray-50" id="contact">
        <Container>
          <SectionTitle
            title="联系我们"
            subtitle="期待与您携手共创美好未来"
            centered
          />
          <div className="grid lg:grid-cols-2 gap-12 mt-16">
            {/* 联系信息 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
            >
              <Card className="p-8 h-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">联系信息</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-[#E6F0FF] text-[#1677FF]">
                      <MapPin className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">公司地址</h4>
                      <p className="text-gray-600">北京市朝阳区望京国际研发园H座1层</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: '#E6F0FF', color: '#1677FF'}}>
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">联系电话</h4>
                      <p className="text-gray-600">400-888-9999</p>
                      <p className="text-gray-600">010-85951234</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: '#E6F0FF', color: '#1677FF'}}>
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">邮箱地址</h4>
                      <p className="text-gray-600">kefu@hard-inc.com</p>
                      <p className="text-gray-600">business@hard-inc.com</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{backgroundColor: '#E6F0FF', color: '#1677FF'}}>
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">工作时间</h4>
                      <p className="text-gray-600">周一至周五 9:00-18:00</p>
                      <p className="text-gray-600">7x24小时技术支持</p>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
            
            {/* 免费咨询表单 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={contactInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="p-8 h-full">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">免费咨询</h3>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* 联系人姓名 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <User className="w-4 h-4 inline mr-2" />
                      联系人姓名 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677FF] focus:border-transparent transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  {/* 联系方式 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-4 h-4 inline mr-2" />
                      联系方式 *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.contactType || 'phone'}
                        onChange={(e) => setFormData({ ...formData, contactType: e.target.value as 'phone' | 'email' })}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677FF] focus:border-transparent"
                      >
                        <option value="phone">手机</option>
                        <option value="email">邮箱</option>
                      </select>
                      <input
                        type={(formData.contactType || 'phone') === 'phone' ? 'tel' : 'email'}
                        required
                        value={formData.contact || ''}
                        onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677FF] focus:border-transparent transition-all"
                        placeholder={(formData.contactType || 'phone') === 'phone' ? '请输入手机号码' : '请输入邮箱地址'}
                      />
                    </div>
                  </div>

                  {/* 公司名称 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Building className="w-4 h-4 inline mr-2" />
                      公司名称 *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677FF] focus:border-transparent transition-all"
                      placeholder="请输入公司名称"
                    />
                  </div>

                  {/* 解决方案选择 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      您希望使用的解决方案 *
                    </label>
                    <select
                      required
                      value={formData.solution || ''}
                      onChange={(e) => setFormData({ ...formData, solution: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677FF] focus:border-transparent transition-all"
                    >
                      <option value="">请选择您感兴趣的解决方案</option>
                      <option value="charging">充电服务解决方案</option>
                      <option value="refueling">加油服务解决方案</option>
                      <option value="maintenance">维修保养解决方案</option>
                      <option value="retail">便利店零售解决方案</option>
                      <option value="rental">租车服务解决方案</option>
                      <option value="custom">定制化解决方案</option>
                    </select>
                  </div>
                  
                  {/* 具体需求描述 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="w-4 h-4 inline mr-2" />
                      具体需求描述
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1677FF] focus:border-transparent transition-all resize-none"
                      placeholder="请详细描述您的需求，我们将为您提供更精准的解决方案"
                    ></textarea>
                  </div>
                  
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#1677FF] hover:opacity-90"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    提交咨询
                  </Button>
                </form>
              </Card>
            </motion.div>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default AboutPage;